// MACHINERY V2 (Stroje) — a toggle CHIP with a legal touch target (M2.16b).
//
// >>> THE PAINTED PILL AND THE HIT TARGET ARE NOT THE SAME BOX, AND THAT IS THE
// WHOLE POINT. <<< The design package draws its page chip rows at h 34
// (mobile/machinery-mobile.jsx MbV3Phone :299-301) and h 40 (MbFilterA :57),
// while the law amended at M2.16a's fix pass is ">= 44px BOTH axes below 1280,
// minWidth accompanies height everywhere it applies". Both are satisfied the way
// ActiveFilterChips.tsx already satisfied them for row 2 of pattern B: the
// BUTTON is 44x44 minimum and fully transparent, and a span inside it paints the
// board's pill, centred in whatever is left over. The row looks like the board
// and touches like the standard.
//
// WHY IT IS A COMPONENT AND ActiveFilterChips IS NOT REFACTORED ONTO IT.
// M2.16b adds TWO page chip rows — V3's ZORADIT sort chips and V2's status
// counters — and a third copy of the same 25 lines is where a kit starts to
// drift. ActiveFilterChips is deliberately left alone: it shipped in M2.16a
// after a review round, its chip is a REMOVE control carrying a decorative ✕ and
// a per-chip aria-label rather than a toggle, and rewriting a reviewed component
// to prove a point is how a "shared primitive" becomes a regression. This is the
// primitive for the toggles; that one stays the primitive for the dismissals.
//
// It does NOT own the scroller. Both consumers wrap it in the 16a
// ChipScroller — one fade, one hidden scrollbar, one trailing spacer, for every
// chip row in the module.
//
// NO ANIMATION AND NO TRANSITION. `machinery-v2-tokens.scss:289-292` forces
// `transition: none !important` on every descendant of `.machinery-v2-root`
// under prefers-reduced-motion, so any transition here would simply not run for
// those users; the active/inactive paints are therefore both correct as RESTING
// states, with nothing depending on the travel between them.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

/** The board's pill height. One constant, because the two consumers must not
 *  drift into 34-vs-36 rows sitting one above the other. */
const PILL_HEIGHT = 34;

export interface TouchChipProps {
  active: boolean;
  onClick: () => void;
  /** Toggle semantics for a filter chip; omit for a chip that is a CHOICE
   *  within a group (the sort row uses `aria-label` + the active paint). */
  ariaPressed?: boolean;
  ariaLabel?: string;
  title?: string;
  /** Painted after the label in mono — the status counters' machine count. */
  count?: number;
  /** Painted after the count — the sort row's ↑ / ↓ direction glyph. */
  trailing?: React.ReactNode;
  children: React.ReactNode;
}

const TouchChip: React.FC<TouchChipProps> = ({
  active,
  onClick,
  ariaPressed,
  ariaLabel,
  title,
  count,
  trailing,
  children,
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={ariaPressed}
    aria-label={ariaLabel}
    title={title}
    style={{
      // 44x44 minimum, transparent — the pill below is what the user sees.
      height: 44,
      minWidth: 44,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      border: 0,
      background: "transparent",
      padding: 0,
      cursor: "pointer",
      fontFamily: "inherit",
    }}
  >
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        height: PILL_HEIGHT,
        padding: "0 12px",
        borderRadius: 999,
        // The module accent, never the brand accent: this is Stroje chrome.
        background: active ? "var(--mod-accent-soft)" : "var(--card)",
        border: "1px solid " + (active ? "var(--mod-accent-line)" : "var(--border)"),
        color: active ? "var(--mod-accent)" : "var(--ink-2)",
        fontSize: 12,
        fontWeight: active ? 600 : 400,
        whiteSpace: "nowrap",
      }}
    >
      {children}
      {count != null && (
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            opacity: active ? 0.85 : 0.7,
          }}
        >
          {count}
        </span>
      )}
      {trailing}
    </span>
  </button>
);

export default TouchChip;
